import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'vnetwork-ui'
import { GridColDefVNW } from 'vnetwork-ui/dist/components/DataGridPro'
import CellDate from '@/components/GokuDataGrid/CellDate'
import { mailerStatusColor } from './cv-status-colors'

// Renders comma-joined list when value is an array, raw string otherwise.
const renderArrayOrString = (params: any) => {
    const value = params?.value as string[] | string
    return Array.isArray(value) ? value.join(', ') : value
}

// Renders a formatted date cell or empty when null.
const renderDateCell = (params: any) => {
    const value = params?.value as string | null
    return value ? <CellDate date={value} scope="HH:mm DD/MM/YYYY" /> : null
}

// Hook returns the GokuDataGrid column definitions for the CV mailer-tracking grid.
// Extracted from ResultCVInformation to keep that component below the cognitive
// complexity threshold flagged by the linter.
export const useCVTrackingColumns = (): GridColDefVNW<any>[] => {
    const { t } = useTranslation()
    return useMemo(
        () => [
            {
                field: 'mailer_subject',
                headerName: t('subject'),
                flex: 1,
                minWidth: 240,
            },
            {
                field: 'mailer_receiver',
                headerName: t('email'),
                width: 220,
                renderCell: renderArrayOrString,
            },
            {
                field: 'mailer_cc',
                headerName: t('cc'),
                width: 180,
                renderCell: renderArrayOrString,
            },
            {
                field: 'mailer_status',
                headerName: t('status'),
                width: 140,
                renderCell: (params: any) => {
                    const value = params?.value as string
                    return (
                        <Chip
                            label={value}
                            color={mailerStatusColor(value)}
                            size="small"
                        />
                    )
                },
            },
            {
                field: 'mailer_send_at',
                headerName: t('send-date'),
                width: 170,
                renderCell: renderDateCell,
            },
            {
                field: 'mailer_performed_at',
                headerName: t('performed-at'),
                width: 170,
                renderCell: renderDateCell,
            },
            {
                field: 'metadata.template',
                headerName: t('template'),
                width: 180,
                valueGetter: (params: any) =>
                    params?.row?.metadata?.template ||
                    params?.row?.mailer_template_id,
            },
            {
                field: 'metadata.name_candidate',
                headerName: t('name'),
                width: 160,
                valueGetter: (params: any) =>
                    params?.row?.metadata?.name_candidate,
            },
            {
                field: 'metadata.email_candidate',
                headerName: t('email-candidate'),
                width: 200,
                valueGetter: (params: any) =>
                    params?.row?.metadata?.email_candidate,
            },
        ],
        [t]
    )
}
